import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { useCurrentUser } from "@/lib/auth";
import { getOrder } from "@/lib/projectOverview";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import {
  ensureOverviewGroups,
  userCanCreateTimesheetsFromOverview,
} from "@/projectOverview/permissions";
import type { PlanungOrderLink } from "@/lib/montageplanung";

function orderDisplayLabel(orderId: number, label: string | null): string {
  return label?.trim() || `Auftrag ${orderId}`;
}

export interface AssignmentProjectActionsProps {
  orders: PlanungOrderLink[];
  compact?: boolean;
}

export function AssignmentProjectActions({ orders, compact }: AssignmentProjectActionsProps) {
  const { user } = useCurrentUser();
  const { openCreateForProject } = useTimesheetFlow();
  const [canCreate, setCanCreate] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    if (!user) {
      setCanCreate(false);
      return;
    }
    void ensureOverviewGroups(user).then((groups) => {
      setCanCreate(userCanCreateTimesheetsFromOverview(user, groups));
    });
  }, [user]);

  const handleCreate = useCallback(
    async (orderId: number) => {
      setBusyId(orderId);
      try {
        const order = await getOrder(orderId);
        openCreateForProject(order);
      } finally {
        setBusyId(null);
      }
    },
    [openCreateForProject],
  );

  if (!orders.length) return null;

  return (
    <ul className={compact ? "space-y-1.5" : "space-y-2"}>
      {orders.map((o) => (
        <li
          key={o.order_id}
          className="flex items-center gap-2 rounded-lg border border-separator bg-fill-quaternary/40 px-2 py-1.5"
        >
          <span className="min-w-0 flex-1 line-clamp-2 break-words text-xs text-label">
            {orderDisplayLabel(o.order_id, o.order_label)}
          </span>
          <ProjectOverviewButton
            auftragId={o.order_id}
            className="h-7 w-7 shrink-0"
            title="Projektübersicht öffnen"
          />
          {canCreate && (
            <Button
              variant="secondary"
              size="sm"
              className="h-7 shrink-0 px-2 text-[11px]"
              disabled={busyId === o.order_id}
              onClick={() => void handleCreate(o.order_id)}
            >
              Nachweis
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
